<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col overflow-hidden">
      <div class="px-4 sm:px-6 pt-3 pb-1">
        <div class="flex items-baseline gap-2 flex-wrap">
          <h2 class="text-base font-bold text-[#0d4169] text-left m-0">
            {{ resolvedCompanyName }}
          </h2>
          <span class="text-sm font-bold text-slate-600">Leaving Request Report</span>
        </div>
      </div>

      <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

      <form class="px-4 sm:px-6 pb-5 pt-2" @submit.prevent="generateReport">
        <div class="flex flex-col lg:flex-row lg:items-end lg:flex-wrap gap-x-4 gap-y-3">
          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[170px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.startDate"
              type="date"
              required
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[170px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">End Date</label>
            <input
              v-model="form.endDate"
              type="date"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[200px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Employee Name</label>
            <input
              v-model="form.employeeName"
              type="text"
              placeholder="Optional"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto sm:min-w-[160px] flex-1 lg:flex-none">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">By Status</label>
            <select
              v-model="form.status"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 w-full sm:w-auto">
            <button
              type="submit"
              :disabled="isLoadingReport"
              class="w-full sm:w-auto px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span v-if="isLoadingReport" class="inline-flex items-center gap-2">
                <span class="h-4 w-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
                Generating...
              </span>
              <span v-else>Generate</span>
            </button>
          </div>
        </div>
      </form>

      <div class="px-2 pb-6 pt-2">
        <template v-if="showReportTable">
          <div v-if="isLoadingReport" class="py-12 flex justify-center items-center">
            <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <div v-else class="w-full">
            <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0 px-2 sm:px-4">
              {{ reportPeriodTitle }}
            </p>

            <div class="leave-table-scroll relative overflow-x-auto overflow-y-auto min-h-[220px] max-h-[calc(100vh-280px)] px-1 sm:px-2">
              <table class="leave-table w-full min-w-[720px] border-collapse">
                <thead>
                  <tr>
                    <th class="leave-col-no">No.</th>
                    <th>Employee Name</th>
                    <th>Leave from</th>
                    <th>Leave to</th>
                    <th>Leave type</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in reportRows"
                    :key="row.id || row.no"
                    class="hover:bg-slate-50 transition-colors border-b"
                  >
                    <td class="leave-col-no">{{ row.no }}</td>
                    <td>{{ row.employeeName || '—' }}</td>
                    <td>{{ formatDisplayDate(row.leaveFrom) }}</td>
                    <td>{{ formatDisplayDate(row.leaveTo) }}</td>
                    <td>{{ row.leaveType || '—' }}</td>
                    <td>
                      <span class="status-pill" :class="statusClass(row)">
                        {{ row.status || statusLabel(row.isApproved) }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="reportRows.length === 0">
                    <td colspan="6" class="text-center text-slate-400 italic py-5">
                      No leaving requests found for the selected filters.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <p
          v-else
          class="text-sm text-slate-500 text-center py-10 m-0 italic"
        >
          Select a Start Date and click Generate to view the leaving request report.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../../finance/FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  startDate: '',
  endDate: '',
  employeeName: '',
  status: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

const isLoadingReport = ref(false)
const reportRows = ref([])
const hasGenerated = ref(false)
let reportRequestId = 0

const showReportTable = computed(() => hasGenerated.value)

function formatDisplayDate(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function statusLabel(isApproved) {
  const n = Number(isApproved)
  if (n === 1) return 'Accepted'
  if (n === -1) return 'Rejected'
  return 'Pending'
}

function statusClass(row) {
  const label = String(row?.status || statusLabel(row?.isApproved)).toLowerCase()
  if (label === 'accepted') return 'accepted'
  if (label === 'rejected') return 'rejected'
  return 'pending'
}

const reportPeriodTitle = computed(() => {
  const start = form.startDate ? formatDisplayDate(form.startDate) : ''
  const end = form.endDate ? formatDisplayDate(form.endDate) : ''
  let title = 'Leaving Request Report'
  if (start && end) title = `Leaving Request Report for ${start} - ${end}`
  else if (start) title = `Leaving Request Report from ${start}`
  if (form.status) title += ` (${form.status})`
  if (form.employeeName.trim()) title += ` — ${form.employeeName.trim()}`
  return title
})

async function generateReport() {
  const startDate = String(form.startDate || '').trim()
  if (!startDate) {
    showToast('Start Date is required.', 'error')
    return
  }

  const endDate = String(form.endDate || '').trim()
  if (endDate && endDate < startDate) {
    showToast('End Date cannot be before Start Date.', 'error')
    return
  }

  const requestId = ++reportRequestId
  isLoadingReport.value = true
  hasGenerated.value = true

  try {
    const params = new URLSearchParams()
    params.set('startDate', startDate)
    if (endDate) params.set('endDate', endDate)
    const employeeName = String(form.employeeName || '').trim()
    if (employeeName) params.set('employeeName', employeeName)
    if (form.status) params.set('status', form.status)

    const response = await apiFetch(`/hr/leaving-request-report?${params.toString()}`, {
      method: 'GET'
    })
    if (requestId !== reportRequestId) return

    if (!response.ok) {
      reportRows.value = []
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to load leaving request report.', 'error')
      return
    }

    const rows = await response.json().catch(() => [])
    reportRows.value = Array.isArray(rows) ? rows : []
  } catch {
    if (requestId !== reportRequestId) return
    reportRows.value = []
    showToast('Failed to load leaving request report.', 'error')
  } finally {
    if (requestId === reportRequestId) {
      isLoadingReport.value = false
    }
  }
}
</script>

<style scoped>
.leave-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
}

.leave-table th,
.leave-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
}

.leave-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 20;
}

.leave-col-no {
  width: 2.75rem;
  min-width: 2.75rem;
  max-width: 2.75rem;
  text-align: center;
}

.leave-table tbody td {
  text-align: left;
}

.leave-table tbody tr:hover {
  background: #f8fafc;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4.5rem;
  padding: 0.15rem 0.55rem;
  border-radius: 9999px;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
}

.status-pill.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-pill.accepted {
  background: #dcfce7;
  color: #166534;
}

.status-pill.rejected {
  background: #fee2e2;
  color: #991b1b;
}

@media (max-width: 640px) {
  .leave-table th,
  .leave-table td {
    padding: 5px 8px;
    font-size: 0.7rem;
  }
}
</style>
